import React, { useState, useEffect, useContext } from 'react';
import './Landing.css';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { withFirebase } from '../Firebase';
import { AuthUserContext } from '../Session';
import waitlistApi from '../../services/waitlistApi';
import * as ROUTES from '../../constants/routes';

// Inline feedback states for the signup form.
const FORM_STATE = {
  IDLE: 'idle',           // showing the email input + submit button
  PENDING: 'pending',     // 202 — confirmation email sent, waiting for user to click link
  VERIFIED: 'verified',   // 200 — email was already on the list
  ERROR: 'error',         // 400 / 502 / network — inline error below the form
};

const Landing360 = ({ firebase }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const authUser = useContext(AuthUserContext);
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [formState, setFormState] = useState(FORM_STATE.IDLE);
  const [errorMessage, setErrorMessage] = useState('');
  const [submittedEmail, setSubmittedEmail] = useState('');

  // Redirect if user is already connected
  useEffect(() => {
    if (authUser) {
      navigate(ROUTES.HOME);
    }
  }, [authUser, navigate]);

  // Track page visit on component mount
  useEffect(() => {
    if (firebase.analytics) {
      firebase.analytics.logEvent('landing_360_visit', {
        page_title: 'Landing 360',
        language: i18n.language || 'en',
        timestamp: new Date().toISOString(),
        user_agent: navigator.userAgent,
        device_type: /mobile|android|iphone/i.test(navigator.userAgent) ? 'mobile' : 'desktop',
        screen_resolution: `${window.screen.width}x${window.screen.height}`,
        referrer: document.referrer || 'direct',
        viewport_height: window.innerHeight,
        viewport_width: window.innerWidth
      });
    }
  }, [firebase, i18n.language]);

  const handleEmailSubmit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();

    // Track signup attempt
    if (firebase.analytics) {
      firebase.analytics.logEvent('landing_360_signup_attempt', {
        email_domain: email.split('@')[1] || 'unknown',
        timestamp: new Date().toISOString()
      });
    }

    // Validate email
    if (!email || !email.includes('@')) {
      setFormState(FORM_STATE.ERROR);
      setErrorMessage(t('waitlist.email_invalid'));

      if (firebase.analytics) {
        firebase.analytics.logEvent('landing_360_signup_error', {
          error_type: 'invalid_email_format',
          timestamp: new Date().toISOString()
        });
      }
      return;
    }

    try {
      setLoading(true);
      setFormState(FORM_STATE.IDLE);
      setErrorMessage('');

      const response = await waitlistApi.subscribe(email);
      const status = response && response.status;

      setSubmittedEmail(email);

      if (status === 'verified') {
        // 200 — already subscribed
        setFormState(FORM_STATE.VERIFIED);
        if (firebase.analytics) {
          firebase.analytics.logEvent('landing_360_signup_success', {
            email_domain: email.split('@')[1] || 'unknown',
            language: i18n.language || 'en',
            device_type: /mobile|android|iphone/i.test(navigator.userAgent) ? 'mobile' : 'desktop',
            result: 'already_verified',
            timestamp: new Date().toISOString()
          });
        }
      } else {
        // 202 (or any other successful response) — pending confirmation
        setFormState(FORM_STATE.PENDING);
        if (firebase.analytics) {
          firebase.analytics.logEvent('landing_360_signup_success', {
            email_domain: email.split('@')[1] || 'unknown',
            language: i18n.language || 'en',
            device_type: /mobile|android|iphone/i.test(navigator.userAgent) ? 'mobile' : 'desktop',
            result: 'pending_confirmation',
            timestamp: new Date().toISOString()
          });
        }
      }
    } catch (error) {
      console.error('Error subscribing email:', error);

      const is400 = error && error.status === 400;
      setFormState(FORM_STATE.ERROR);

      if (is400) {
        // Backend rejected the email (malformed / disposable / etc.)
        setErrorMessage(t('waitlist.email_invalid'));
      } else {
        // 502 / network / timeout / other
        setErrorMessage(t('waitlist.subscribe_error'));
      }

      if (firebase.analytics) {
        firebase.analytics.logEvent('landing_360_signup_error', {
          error_type: is400 ? 'invalid_email' : 'server_error',
          error_status: error?.status || 'network',
          error_message: error?.message || 'unknown',
          timestamp: new Date().toISOString()
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleBackToForm = () => {
    setFormState(FORM_STATE.IDLE);
    setErrorMessage('');
    setSubmittedEmail('');
    setEmail('');
  };

  const handleEmailFocus = () => {
    // Track user engagement with email input
    if (firebase.analytics) {
      firebase.analytics.logEvent('landing_360_email_focus', {
        timestamp: new Date().toISOString()
      });
    }
  };

  const isFormVisible = formState === FORM_STATE.IDLE || formState === FORM_STATE.ERROR;

  return (
    <div className="landing-page" style={{scrollSnapType: "unset"}}>
      <div className="hero-content" style={{margin: "auto", padding: "1rem", paddingTop: "2vh"}}> 
        <div className="hero-badge" aria-label="Tower crane">
          {t('landing.hero_badge_private')}
        </div>
        <h3 className="hero-headline">{t('landing.site_under_construction')}</h3>
        <span className="hero-subheadline">
          {t('landing.hero_subheadline_360')}
        </span>
                <Link to={ROUTES.BLOG} className="blog-link">
            {t('landing.see_blog', 'See Blog')}
        </Link>
        <blockquote className="hero-subheadline" style={{fontSize: '0.8rem', padding:'0 0.5rem', marginTop: '1rem'}}>
          {t('landing.long_description_360')}
        </blockquote>

        {/* Two-audience CTA row: primary path for new testers, ghost path for the beta team. */}
        <div className="hero-actions" role="group" aria-label="Access the app — sign in for beta team, sign up for new testers">
          <Link to={ROUTES.SIGN_UP} className="btn-primary hero-actions__btn">
            {t('landing.sign_up_button')}
          </Link>
          <Link to={ROUTES.SIGN_IN} className="btn-secondary hero-actions__btn">
            {t('landing.sign_in_button')}
          </Link>
        </div>

        {isFormVisible && (
          <div className="hero-cta">
            <input
              id="email-input"
              type="email"
              name="email"
              placeholder={t('landing.email_placeholder')}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onFocus={handleEmailFocus}
              disabled={loading}
              required
            />
            <button 
              onClick={handleEmailSubmit}
              disabled={loading}
              style={{ cursor: loading ? 'not-allowed' : 'pointer' }}
            >
              {loading ? t('landing.loading') || 'Loading...' : t('landing.get_notified')}
            </button>
          </div>
        )}

        {formState === FORM_STATE.ERROR && errorMessage && (
          <div
            className="form-success-message"
            role="alert"
            style={{ color: 'var(--pico-del-color, #d7373c)', marginTop: '0.75rem' }}
          >
            {errorMessage}
          </div>
        )}

        {formState === FORM_STATE.PENDING && (
          <div
            className="form-success-message"
            role="status"
            aria-live="polite"
            style={{ marginTop: '1rem' }}
          >
            <p style={{ margin: '0 0 0.75rem' }}>
              ✓ {t('waitlist.check_inbox', { email: submittedEmail })}
            </p>
            <button
              type="button"
              onClick={handleBackToForm}
              style={{
                background: 'transparent',
                border: 'var(--pico-border-width) solid var(--pico-muted-border-color)',
                borderRadius: 'var(--pico-border-radius)',
                padding: '0.4rem 0.9rem',
                fontSize: '0.85rem',
                color: 'var(--pico-muted-color)',
                cursor: 'pointer',
              }}
            >
              ← {t('waitlist.back_to_form')}
            </button>
          </div>
        )}

        {formState === FORM_STATE.VERIFIED && (
          <div
            className="form-success-message"
            role="status"
            aria-live="polite"
            style={{ marginTop: '1rem' }}
          >
            <p style={{ margin: '0 0 0.75rem' }}>
              ✓ {t('waitlist.already_subscribed')}
            </p>
            <button
              type="button"
              onClick={handleBackToForm}
              style={{
                background: 'transparent',
                border: 'var(--pico-border-width) solid var(--pico-muted-border-color)',
                borderRadius: 'var(--pico-border-radius)',
                padding: '0.4rem 0.9rem',
                fontSize: '0.85rem',
                color: 'var(--pico-muted-color)',
                cursor: 'pointer',
              }}
            >
              ← {t('waitlist.back_to_form')}
            </button>
          </div>
        )}
      </div>

      <footer className="footer">
        <div className="footer-content">
          <p>© {new Date().getFullYear()} Replan360.com — {t('landing.footer_tagline')}</p>
        </div>
      </footer>
    </div>
  );
};

export default withFirebase(Landing360);
